import { useLocation, useSearchParams } from 'react-router';
import type { CapiNews } from 'types/capi/news';

import type { Crumb } from '~/components/BreadCrumbs';
import PageHeader from '~/components/PageHeader';
import Pill from '~/components/Pill';
import usePageData from '~/hooks/usePageData';
import useStructuredData from '~/hooks/useStructuredData';
import Grid from '~/modules/ArticlesSection/Grid';
import Pagination from '~/routes/plps/components/Products/Pagination';
import { appendTrailingSlash } from '~/utils/trailingSlash';

import NewsHub from './NewsHub';

export const FEED_MODULE_TYPES = {
  search: 'news-search-results'
};

type NewsSection = { label: string; nonGendered?: boolean; value: string };
type NewsGender = { label: string; value: string };

export const NEWS_SECTIONS: NewsSection[] = [
  { label: 'All', value: '' },
  { label: 'Features', nonGendered: true, value: 'features' },
  { label: 'Footwear', value: 'footwear' },
  { label: 'Clothing', value: 'clothing' },
  { label: 'Lifestyle', nonGendered: true, value: 'lifestyle' },
  { label: 'Size guides', nonGendered: true, value: 'size-guides' }
];

export const NEWS_GENDERS: NewsGender[] = [
  { label: 'All', value: '' },
  { label: 'Men', value: 'mens' },
  { label: 'Women', value: 'womens' }
];

function buildNewsPath(genderValue: string, sectionValue: string) {
  const segments = [genderValue, 'news', sectionValue].filter(Boolean);

  return `/${segments.join('/')}/`;
}

function findSection(segments: string[]) {
  return (
    NEWS_SECTIONS.find(
      (section) => section.value && segments.includes(section.value)
    ) ?? NEWS_SECTIONS[0]!
  );
}

function findGender(segments: string[]) {
  return (
    NEWS_GENDERS.find(
      (gender) => gender.value && segments.includes(gender.value)
    ) ?? NEWS_GENDERS[0]!
  );
}

function parsePage(raw: string | null) {
  const page = Number(raw);

  return Number.isInteger(page) && page > 0 ? page : 1;
}

export default function NewsFeedView() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { _schemas, articles, feature, pagination, title } =
    usePageData<CapiNews>();

  const searchTerm = searchParams.get('q')?.trim();
  const isFirstPage = parsePage(searchParams.get('page')) === 1;

  const segments = appendTrailingSlash(pathname).split('/').filter(Boolean);
  const section = findSection(segments);
  const gender = findGender(segments);
  const isIndex = !section.value && !gender.value;

  const crumbs: Crumb[] = isIndex ? [] : [{ label: 'News', path: '/news/' }];

  if (searchTerm) {
    return (
      <div className="flex min-h-[80vh] flex-col pb-12">
        {_schemas ? <StructuredData schemas={_schemas} /> : null}
        <PageHeader breadcrumbs={crumbs} title={title} />
        <div className="flex flex-1 flex-col gap-8 py-8">
          {articles?.length ? (
            <Grid
              items={articles}
              moduleType={FEED_MODULE_TYPES.search}
              pagePosition="News Search Results"
            />
          ) : (
            <NewsSearchEmpty searchTerm={searchTerm} />
          )}
          {pagination.pages ? (
            <div className="content-width">
              <Pagination pagination={pagination} />
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-grey-tile pb-12">
      {_schemas ? <StructuredData schemas={_schemas} /> : null}
      <PageHeader
        actions={<NewsFilters gender={gender} section={section} />}
        breadcrumbs={crumbs}
        hasFilters
        title={title}
      />
      <NewsHub
        articles={articles ?? []}
        feature={feature}
        isFirstPage={isFirstPage}
      />
      {pagination.pages ? (
        <div className="content-width">
          <Pagination pagination={pagination} />
        </div>
      ) : null}
    </div>
  );
}

function NewsSearchEmpty({ searchTerm }: { searchTerm: string }) {
  return (
    <p className="content-width my-auto text-center text-light-grey">
      {`No articles found for “${searchTerm}”.`}
    </p>
  );
}

export function StructuredData({ schemas }: { schemas: object[] }) {
  useStructuredData(schemas);

  return null;
}

type FilterOption = { active: boolean; label: string; to: string };

function NewsFilters({
  gender,
  section
}: {
  gender: NewsGender;
  section: NewsSection;
}) {
  const activeGender = section.nonGendered ? '' : gender.value;

  const categoryOptions: FilterOption[] = NEWS_SECTIONS.map((option) => ({
    active: option.value === section.value,
    label: option.label,
    to: buildNewsPath(option.nonGendered ? '' : activeGender, option.value)
  }));

  return (
    <div className="no-scrollbar flex h-10 items-center gap-2 overflow-x-auto max-md:-mx-4 max-md:px-4 md:justify-end">
      {categoryOptions.map(({ active, label, to }) => (
        <Pill active={active} key={to} to={to}>
          {label}
        </Pill>
      ))}
    </div>
  );
}
